import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Save, User as UserIcon, Mail, Copy, Bell, Phone, Percent } from "lucide-react";

const ALIQUOTE_IVA = [
  { value: 0, label: "0% — Esente / Non imponibile" },
  { value: 4, label: "4% — Ridotta minima" },
  { value: 5, label: "5% — Ridotta" },
  { value: 10, label: "10% — Ridotta" },
  { value: 22, label: "22% — Ordinaria" },
];

// Validazione formato telefono italiano: opzionale +39/0039 + 9-11 cifre.
const PHONE_IT_RE = /^(?:\+?39|0039)?[0-9]{9,11}$/;
const cleanPhone = (v) => (v || "").replace(/[\s\-.()]/g, "");

export default function AccountProfile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ nome: "", cognome: "", telefono: "", aliquota_iva_default: 22 });
  const [saving, setSaving] = useState(false);
  const [companyInbound, setCompanyInbound] = useState("");
  const [fiscalData, setFiscalData] = useState(null);
  const [fiscalSource, setFiscalSource] = useState(""); // "azienda" | "studio"
  const [prefs, setPrefs] = useState(null);
  const [savingPref, setSavingPref] = useState(null);
  const [phoneError, setPhoneError] = useState("");

  useEffect(() => {
    if (user) {
      const parts = (user.full_name || "").split(" ");
      setForm((f) => ({
        ...f,
        nome: user.nome || parts.slice(0, -1).join(" ") || parts[0] || "",
        cognome: user.cognome || (parts.length > 1 ? parts[parts.length - 1] : ""),
      }));
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    // Fonte dati fiscali:
    //   - Utente normale → GET /company (la sua azienda attiva)
    //   - Commercialista → user.billing_info (dati studio)
    // Coerente con come `platform_invoicing.emit_platform_invoice` sceglie i
    // dati per la fattura di vendita al checkout.
    // Precarica anche /auth/me/full per avere telefono + aliquota utente
    api.get("/auth/me/full")
      .then((r) => {
        if (cancelled) return;
        const meFull = r.data || {};
        setForm((f) => ({
          ...f,
          telefono: meFull.telefono || f.telefono || "",
          aliquota_iva_default:
            meFull.aliquota_iva_default !== null && meFull.aliquota_iva_default !== undefined
              ? Number(meFull.aliquota_iva_default)
              : f.aliquota_iva_default,
        }));
      })
      .catch(() => {});

    if (user?.is_commercialista) {
      // Chiedi al backend il profilo completo per avere billing_info
      api.get("/auth/me/full").then((r) => {
        if (cancelled) return;
        setFiscalData(r.data?.billing_info || {});
        setFiscalSource("studio");
      }).catch(() => {
        if (cancelled) return;
        setFiscalData({});
        setFiscalSource("studio");
      });
    } else {
      api.get("/company")
        .then((r) => {
          if (cancelled) return;
          setCompanyInbound(r.data?.inbound_email || "");
          setFiscalData(r.data || {});
          setFiscalSource("azienda");
          // Fallback: se l'utente non ha ancora impostato telefono/aliquota
          // sul proprio profilo, mostriamo quelli dell'azienda attiva.
          setForm((f) => ({
            ...f,
            telefono: f.telefono || r.data?.telefono || "",
            aliquota_iva_default:
              f.aliquota_iva_default !== 22
                ? f.aliquota_iva_default
                : r.data?.aliquota_iva_default !== null && r.data?.aliquota_iva_default !== undefined
                ? Number(r.data.aliquota_iva_default)
                : 22,
          }));
        })
        .catch(() => {});
    }
    api.get("/user/preferences")
      .then((r) => { if (!cancelled) setPrefs(r.data); })
      .catch(() => { if (!cancelled) setPrefs({ weekly_digest: true, critical_alerts: true }); });
    return () => { cancelled = true; };
  }, [user?.is_commercialista]);

  const togglePref = async (key) => {
    if (!prefs) return;
    const nextVal = !prefs[key];
    setSavingPref(key);
    // Optimistic UI
    setPrefs({ ...prefs, [key]: nextVal });
    try {
      const { data } = await api.put("/user/preferences", { [key]: nextVal });
      setPrefs(data);
      toast.success("Preferenze email aggiornate");
    } catch (e) {
      setPrefs({ ...prefs, [key]: !nextVal }); // revert
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSavingPref(null);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onPhoneChange = (e) => {
    const v = e.target.value;
    setForm({ ...form, telefono: v });
    // Validazione soft in tempo reale (non blocca digitazione)
    const cleaned = cleanPhone(v);
    if (cleaned && !PHONE_IT_RE.test(cleaned)) {
      setPhoneError("Formato non valido. Usa +39 o 0 seguito da 9–11 cifre.");
    } else {
      setPhoneError("");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    // Validazione hard prima dell'invio
    const cleanedPhone = cleanPhone(form.telefono);
    if (cleanedPhone && !PHONE_IT_RE.test(cleanedPhone)) {
      setPhoneError("Formato non valido. Usa +39 o 0 seguito da 9–11 cifre.");
      toast.error("Numero di telefono non valido");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/profile", {
        nome: form.nome.trim(),
        cognome: form.cognome.trim(),
        telefono: cleanedPhone,
        aliquota_iva_default: Number(form.aliquota_iva_default),
      });
      await refresh?.();
      toast.success("Profilo aggiornato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  const copy = (v) => {
    navigator.clipboard.writeText(v || "");
    toast.success("Copiato");
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="account-profile-form">
      <section className="rounded-xl border border-[#E8E4D9] bg-white p-6">
        <h2 className="font-display font-semibold text-lg mb-1">Dati Profilo</h2>
        <p className="text-sm text-zinc-500 mb-5">Modifica il tuo nome e cognome. L&apos;email non è modificabile.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Nome">
            <IconInput
              icon={<UserIcon className="w-4 h-4" />}
              value={form.nome}
              onChange={upd("nome")}
              placeholder="Il tuo nome"
              testid="profile-nome"
            />
          </Field>
          <Field label="Cognome">
            <IconInput
              icon={<UserIcon className="w-4 h-4" />}
              value={form.cognome}
              onChange={upd("cognome")}
              placeholder="Il tuo cognome"
              testid="profile-cognome"
            />
          </Field>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4">
          <Field label="Email (non modificabile)">
            <div className="flex items-center gap-2 rounded-md border border-[#E8E4D9] bg-[#F5F2EA] h-11 px-3 text-sm text-zinc-700">
              <Mail className="w-4 h-4 text-zinc-400" />
              <span className="flex-1 truncate font-mono" data-testid="profile-email">{user?.email}</span>
              <button
                type="button"
                onClick={() => copy(user?.email)}
                className="text-zinc-400 hover:text-zinc-700"
                aria-label="Copia email"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </Field>
        </div>
      </section>

      <section className="rounded-xl border border-[#E8E4D9] bg-white p-6" data-testid="account-preferences-defaults">
        <h2 className="font-display font-semibold text-lg mb-1">Preferenze predefinite</h2>
        <p className="text-sm text-zinc-500 mb-5">
          {user?.is_commercialista
            ? "Telefono di contatto e aliquota IVA di default. L'aliquota viene applicata come suggerimento quando crei una nuova azienda cliente (modificabile per singolo cliente)."
            : "Telefono e aliquota IVA predefinita. L'aliquota viene applicata automaticamente alle nuove autofatture della tua azienda attiva."}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Numero di telefono (formato italiano)">
            <IconInput
              icon={<Phone className="w-4 h-4" />}
              value={form.telefono}
              onChange={onPhoneChange}
              placeholder="+39 333 1234567"
              testid="profile-telefono"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
            />
            {phoneError && (
              <p className="mt-1 text-xs text-red-600" data-testid="profile-telefono-error">{phoneError}</p>
            )}
          </Field>
          <Field label="Aliquota IVA predefinita">
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
                <Percent className="w-4 h-4" />
              </div>
              <select
                data-testid="profile-aliquota"
                value={form.aliquota_iva_default}
                onChange={(e) => setForm({ ...form, aliquota_iva_default: Number(e.target.value) })}
                className="w-full h-11 pl-9 pr-3 rounded-md border border-[#E8E4D9] bg-white text-sm focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]/10 outline-none transition-colors appearance-none"
              >
                {ALIQUOTE_IVA.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
            </div>
          </Field>
        </div>
      </section>

      <section className="rounded-xl border border-[#E8E4D9] bg-white p-6" data-testid="account-fiscal-data">
        <div className="flex items-start justify-between gap-4 mb-1">
          <div>
            <h2 className="font-display font-semibold text-lg">Dati fiscali {fiscalSource === "studio" ? "(studio commercialista)" : "(azienda)"}</h2>
            <p className="text-sm text-zinc-500 mt-1">
              Questi dati vengono usati per emettere la fattura elettronica al momento dell&apos;acquisto crediti. <strong>Non sono modificabili da questa pagina</strong>: per correzioni contatta il supporto.
            </p>
          </div>
          <a
            href="mailto:supporto@dasabo.com?subject=Modifica%20dati%20fiscali"
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#E8E4D9] bg-[#F5F2EA] text-xs font-semibold text-[#0A0A0A] hover:bg-[#E8E4D9]"
            data-testid="fiscal-contact-support"
          >
            <Mail className="w-3.5 h-3.5" /> Contatta supporto
          </a>
        </div>
        {fiscalData === null ? (
          <div className="mt-3 flex items-center gap-2 text-sm text-zinc-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            <ReadOnly label="Ragione sociale / Denominazione" value={fiscalData.denominazione} testid="fiscal-denominazione" full />
            <ReadOnly label="Partita IVA" value={fiscalData.partita_iva} testid="fiscal-piva" mono />
            <ReadOnly label="Codice fiscale" value={fiscalData.codice_fiscale} testid="fiscal-cf" mono />
            <ReadOnly label="Indirizzo" value={fiscalData.indirizzo} testid="fiscal-indirizzo" full />
            <ReadOnly label="CAP" value={fiscalData.cap} testid="fiscal-cap" mono />
            <ReadOnly label="Comune" value={fiscalData.comune} testid="fiscal-comune" />
            <ReadOnly label="Provincia" value={fiscalData.provincia} testid="fiscal-provincia" mono />
            <ReadOnly label="Nazione" value={fiscalData.nazione || "IT"} testid="fiscal-nazione" mono />
            <ReadOnly label="Codice destinatario SDI" value={fiscalData.codice_destinatario} testid="fiscal-cd" mono />
            <ReadOnly label="PEC" value={fiscalData.email_pec} testid="fiscal-pec" mono />
          </div>
        )}
      </section>

      {!user?.is_commercialista && (
        <section className="rounded-xl border border-[#E8E4D9] bg-white p-6">
          <h2 className="font-display font-semibold text-lg mb-1">Email dedicata ricezione fatture</h2>
          <p className="text-sm text-zinc-500 mb-4">
            Indirizzo unico della tua azienda. Inoltra qui — o configura direttamente presso Google/Meta/AWS — le fatture estere per l&apos;elaborazione automatica.
          </p>
          <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 h-11 px-3 text-sm">
            <Mail className="w-4 h-4 text-[#1D4ED8]" />
            <span className="flex-1 truncate font-mono text-blue-900" data-testid="profile-inbound-email">{companyInbound || "—"}</span>
            <button
              type="button"
              onClick={() => copy(companyInbound)}
              disabled={!companyInbound}
              className="text-[#1D4ED8] hover:text-blue-800 disabled:opacity-40"
              aria-label="Copia inbound email"
            >
            <Copy className="w-4 h-4" />
          </button>
        </div>
        <p className="mt-2 text-[11px] text-zinc-500">
          Se hai più aziende, ognuna ha un suo indirizzo dedicato: gestiscili dalla sezione <span className="font-medium">Aziende</span>.
        </p>
      </section>
      )}

      <section className="rounded-xl border border-[#E8E4D9] bg-white p-6" data-testid="account-email-prefs">
        <div className="flex items-center gap-2 mb-1">
          <Bell className="w-4 h-4 text-[#1D4ED8]" />
          <h2 className="font-display font-semibold text-lg">Preferenze email</h2>
        </div>
        <p className="text-sm text-zinc-500 mb-4">
          Scegli quali comunicazioni ricevere via email. Puoi cambiare in qualsiasi momento.
        </p>
        {prefs === null ? (
          <div className="flex items-center gap-2 text-sm text-zinc-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
          </div>
        ) : (
          <div className="space-y-3">
            <PrefToggle
              label="Digest settimanale"
              hint="Lunedì mattina: riepilogo autofatture della settimana e stato SDI"
              value={prefs.weekly_digest}
              onToggle={() => togglePref("weekly_digest")}
              saving={savingPref === "weekly_digest"}
              testid="pref-weekly-digest"
            />
            <PrefToggle
              label="Alert critici giornalieri"
              hint="Ogni giorno: solo se ci sono scarti SDI o crediti in esaurimento"
              value={prefs.critical_alerts}
              onToggle={() => togglePref("critical_alerts")}
              saving={savingPref === "critical_alerts"}
              testid="pref-critical-alerts"
            />
          </div>
        )}
      </section>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          data-testid="profile-save-btn"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-[#1D4ED8] text-white font-semibold text-sm hover:bg-[#1E3A8A] shadow-sm shadow-blue-600/20 disabled:opacity-70 transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salva modifiche
        </button>
      </div>
    </form>
  );
}

function PrefToggle({ label, hint, value, onToggle, saving, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-[#0A0A0A]">{label}</div>
        <p className="text-xs text-zinc-500 mt-0.5">{hint}</p>
      </div>
      <button
        type="button"
        onClick={onToggle}
        disabled={saving}
        role="switch"
        aria-checked={!!value}
        data-testid={testid}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
          value ? "bg-[#1D4ED8]" : "bg-zinc-300"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow ${
            value ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
        {saving && <Loader2 className="w-3 h-3 animate-spin absolute inset-0 m-auto text-white" />}
      </button>
    </div>
  );
}

function ReadOnly({ label, value, testid, mono, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">{label}</div>
      <div
        data-testid={testid}
        className={`mt-1 px-3 py-2 rounded-md bg-[#F5F2EA] border border-[#E8E4D9] text-sm text-zinc-800 ${mono ? "font-mono" : ""}`}
      >
        {value || <span className="text-zinc-400 italic">—</span>}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-zinc-800 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function IconInput({ icon, testid, ...props }) {
  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">{icon}</div>
      <input
        data-testid={testid}
        className="w-full h-11 pl-9 pr-3 rounded-md border border-[#E8E4D9] bg-white text-sm focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]/10 outline-none transition-colors"
        {...props}
      />
    </div>
  );
}
